import axios from "axios";
import { store } from "../../store";
import { refreshAccessToken, setIsAuthenticated } from "./authSlice";
import { isLoggedIn } from "./authService";

// Create an Axios instance
const api = axios.create({
  baseURL: window.env.API_URL, // Use your API base URL
});

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
const requestQueue: Array<(token: string) => void> = [];

api.interceptors.request.use(
  async config => {
    const { access } = store.getState().auth;
    if (access) {
      config.headers.Authorization = `Bearer ${access}`;
    }

    if (access && !isLoggedIn(access)) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = store.dispatch(refreshAccessToken())
          .unwrap()
          .then(() => {
            const newToken = store.getState().auth.access!;
            requestQueue.forEach(cb => cb(newToken));
            requestQueue.length = 0;
            return newToken;
          })
          .catch(err => {
            requestQueue.forEach(cb => cb(''));
            requestQueue.length = 0;
            store.dispatch(setIsAuthenticated(false));
            throw err;
          })
          .finally(() => {
            isRefreshing = false;
            refreshPromise = null;
          });
      }

      return new Promise(resolve => {
        requestQueue.push((token: string) => {
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(config);
          } else {
            resolve(Promise.reject({ response: 'Refresh failed' }));
          }
        });
      });
    }

    return config;
  },
  err => Promise.reject(err)
);

api.interceptors.response.use(
  res => res,
  async error => {
    const orig = error.config;
    if (error.response?.status === 401 && !orig._retry) {
      orig._retry = true;
      try {
        const newToken = await store.dispatch(refreshAccessToken()).unwrap();
        orig.headers.Authorization = `Bearer ${newToken}`;
        return api(orig);
      } catch {
        store.dispatch(setIsAuthenticated(false));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;